import { Injectable } from '@angular/core';
import { FirebaseMessaging } from '@capacitor-firebase/messaging';
import { Platform } from '@ionic/angular';
import { environment } from '@environments/environment';
import { HttpService } from '@core/http/http.service';

/** Cap for removing the token on logout: without network, logout doesn't wait. */
const UNREGISTER_TIMEOUT_MS = 3000;

/**
 * FCM token of this install in the API (`push-devices`). Lives apart from
 * NotificationService so the logout screens can remove it without pulling in the
 * listeners.
 */
@Injectable({
  providedIn: 'root',
})
export class PushRegistrationService {
  /** There is a session with notification permission: new tokens get registered. */
  private enabled = false;
  private registeredToken: string | null = null;

  constructor(
    private api: HttpService,
    private platform: Platform,
  ) {}

  /** Registers the current token. Idempotent: the same token is not sent twice. */
  async enable(): Promise<void> {
    this.enabled = true;

    try {
      const { token } = await FirebaseMessaging.getToken();
      await this.register(token);
    } catch {
      // iOS may not have the APNs token yet: it arrives later through tokenReceived.
    }
  }

  /** FCM rotated the token (or delivered it late): only counts with a session. */
  async tokenChanged(token: string): Promise<void> {
    if (this.enabled) {
      await this.register(token).catch(() => undefined);
    }
  }

  /**
   * Logout: removes the token in the API (while the session token is still stored)
   * and deletes the FCM token, which stops working even if the removal fails.
   * Call it before clearing localStorage.
   */
  async disable(): Promise<void> {
    const token = this.registeredToken;
    this.enabled = false;
    this.registeredToken = null;

    if (!this.platform.is('capacitor')) return;

    const timeout = new Promise<void>((resolve) => setTimeout(resolve, UNREGISTER_TIMEOUT_MS));
    const unregister = (async () => {
      if (token) {
        await this.api.delete('push-devices', { token });
      }
      await FirebaseMessaging.deleteToken();
    })().catch(() => undefined);

    await Promise.race([unregister, timeout]);
  }

  private async register(token: string | undefined): Promise<void> {
    if (!token || token === this.registeredToken) return;

    const res = (await this.api.put('push-devices', {
      token,
      platform: this.platform.is('ios') ? 'ios' : 'android',
      app_version: environment.version,
    })) as { success?: boolean } | null;

    if (res?.success) {
      this.registeredToken = token;
    }
  }
}
